import React, { useState, useEffect } from 'react';
import { getRules, updateRule, deleteRule, initializeRules } from '../services/driftService';
import { track as trackTelemetry } from '../services/telemetryService';
import { getProfile, updateProfile } from '../services/profileService';
import { SEVERITY_OPTIONS } from '../services/alertService';
import DriftRuleBuilder from '../components/DriftRuleBuilder';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { HelpIcon } from '../components/common';
import './DriftRules.css';

const PACK_DESCRIPTIONS = {
  'Essential Security': 'Core security monitoring for critical vulnerabilities, certificate issues, and dangerous port exposure',
  'Network Monitoring': 'Track all port and service changes across your assets',
  'Certificate Management': 'Advanced certificate monitoring including issuer changes and weak TLS detection',
  'Compliance Readiness': 'Monitor for compliance-relevant configurations including admin panels',
  'DNS Monitoring': 'Track DNS record changes including nameserver, MX, and TXT modifications',
  'Registration Monitoring': 'Monitor domain registration changes including registrar transfers, expiry warnings, and status flags',
  'Technology Monitoring': 'Track technology stack changes including new detections, version updates, and removals',
  'Discovery': 'Catch newly exposed services and databases the moment they appear on your surface',
  'Custom': 'Your custom drift alarm rules'
};

// Render packs in catalog order (the API sorts alphabetically); unknown packs fall
// to the end, Custom always last.
const PACK_ORDER = [
  'Essential Security', 'Network Monitoring', 'Certificate Management',
  'Compliance Readiness', 'DNS Monitoring', 'Registration Monitoring',
  'Technology Monitoring', 'Discovery', 'Custom'
];

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60000) return 'just now';
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function DriftRules() {
  // Drift rules state
  const [driftRules, setDriftRules] = useState([]);
  const [driftRulesLoading, setDriftRulesLoading] = useState(true);
  const [driftRulesError, setDriftRulesError] = useState(null);
  const [driftRuleSaving, setDriftRuleSaving] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [expandedPacks, setExpandedPacks] = useState({ 'Essential Security': true });
  const [expandedRules, setExpandedRules] = useState({});
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);

  // Global severity threshold (userprofile.alarmSeverityThreshold) — mirrors the
  // control shipped on /settings in Stage 4.
  const [userProfile, setUserProfile] = useState(null);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  // Load drift rules (extracted so Retry button can call it directly)
  const loadDriftRules = async () => {
    try {
      setDriftRulesLoading(true);
      setDriftRulesError(null);
      let rules = await getRules();
      if (rules.length === 0) {
        await initializeRules();
        rules = await getRules();
      }
      setDriftRules(rules);
    } catch (err) {
      console.error('Failed to load drift rules:', err);
      setDriftRulesError('Failed to load drift alarm rules. Please try again.');
    } finally {
      setDriftRulesLoading(false);
    }
  };

  useEffect(() => {
    loadDriftRules();
    getProfile().then(setUserProfile).catch(err => {
      console.error('Failed to load profile for severity threshold:', err);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyRulePatch = (ruleId, patch) =>
    setDriftRules(prev => prev.map(r => (r.ruleId === ruleId ? { ...r, ...patch } : r)));

  // Alarm toggle. Turning the alarm OFF cascades the email OFF in a single PUT —
  // the backend rejects email-on while alarm-off (you can't email about a rule you've
  // disabled), so the UI mirrors that invariant.
  const handleAlarmToggle = async (rule, next) => {
    const snapshot = { alarmEnabled: rule.alarmEnabled, emailEnabled: rule.emailEnabled };
    const patch = next ? { alarmEnabled: true } : { alarmEnabled: false, emailEnabled: false };
    setDriftRuleSaving(rule.ruleId);
    setActionError(null);
    applyRulePatch(rule.ruleId, patch);
    try {
      await updateRule(rule.ruleId, patch);
      // PRD-151 WP2: config change telemetry (enums only, no rule id/target).
      try { trackTelemetry('action', { meta: { feature: 'config', action: 'rule_toggle', detail: 'alarm' } }); } catch (e) { /* best-effort */ }
    } catch (err) {
      applyRulePatch(rule.ruleId, snapshot);
      setActionError(err.message || 'Failed to update rule');
    } finally {
      setDriftRuleSaving(null);
    }
  };

  // Email toggle — only reachable when the alarm is on (toggle is disabled otherwise).
  const handleEmailToggle = async (rule, next) => {
    const snapshot = { emailEnabled: rule.emailEnabled };
    setDriftRuleSaving(rule.ruleId);
    setActionError(null);
    applyRulePatch(rule.ruleId, { emailEnabled: next });
    try {
      await updateRule(rule.ruleId, { emailEnabled: next });
      // PRD-151 WP2: config change telemetry (enums only, no rule id/target).
      try { trackTelemetry('action', { meta: { feature: 'config', action: 'rule_toggle', detail: 'email' } }); } catch (e) { /* best-effort */ }
    } catch (err) {
      applyRulePatch(rule.ruleId, snapshot);
      setActionError(err.message || 'Failed to update rule');
    } finally {
      setDriftRuleSaving(null);
    }
  };

  const handleCadenceChange = async (rule, cadence) => {
    if (rule.emailCadence === cadence) return;
    const snapshot = { emailCadence: rule.emailCadence };
    setDriftRuleSaving(rule.ruleId);
    setActionError(null);
    applyRulePatch(rule.ruleId, { emailCadence: cadence });
    try {
      await updateRule(rule.ruleId, { emailCadence: cadence });
    } catch (err) {
      applyRulePatch(rule.ruleId, snapshot);
      setActionError(err.message || 'Failed to update cadence');
    } finally {
      setDriftRuleSaving(null);
    }
  };

  // Rule #4 day-threshold (conditionValueOptions). Persists the chosen value;
  // by-day matching is wired in a later stage.
  const handleCertThresholdChange = async (rule, value) => {
    const snapshot = { conditionValue: rule.conditionValue };
    setDriftRuleSaving(rule.ruleId);
    setActionError(null);
    applyRulePatch(rule.ruleId, { conditionValue: value });
    try {
      await updateRule(rule.ruleId, { conditionValue: value });
    } catch (err) {
      applyRulePatch(rule.ruleId, snapshot);
      setActionError(err.message || 'Failed to update threshold');
    } finally {
      setDriftRuleSaving(null);
    }
  };

  const handleSeverityThresholdChange = async (value) => {
    const prev = userProfile?.alarmSeverityThreshold || 'medium';
    setThresholdSaving(true);
    setActionError(null);
    setUserProfile(p => ({ ...(p || {}), alarmSeverityThreshold: value }));
    try {
      await updateProfile({ alarmSeverityThreshold: value });
    } catch (err) {
      setUserProfile(p => ({ ...(p || {}), alarmSeverityThreshold: prev }));
      setActionError(err.message || 'Failed to update severity threshold');
    } finally {
      setThresholdSaving(false);
    }
  };

  // Handle custom rule deletion
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this custom rule?')) return;
    try {
      setDriftRuleSaving(ruleId);
      setActionError(null);
      await deleteRule(ruleId);
      setDriftRules(prev => prev.filter(r => r.ruleId !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule:', err);
      setActionError(err.message || 'Failed to delete rule');
    } finally {
      setDriftRuleSaving(null);
    }
  };

  // Group rules by pack, then order packs by catalog order.
  const rulesByPack = driftRules.reduce((acc, rule) => {
    const pack = rule.pack || 'Custom';
    if (!acc[pack]) acc[pack] = [];
    acc[pack].push(rule);
    return acc;
  }, {});
  const orderedPacks = Object.keys(rulesByPack).sort((a, b) => {
    const ia = PACK_ORDER.indexOf(a);
    const ib = PACK_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const totalAlarming = driftRules.filter(r => r.alarmEnabled && !r.comingSoon).length;
  const currentThreshold = userProfile?.alarmSeverityThreshold || 'medium';

  return (
    <CleanModernLayout activePage="drift-rules">
      <div className="drift-rules-page">
        <div className="drift-rules-header">
          <div className="drift-rules-title-row">
            <h1>
              <span className="drift-rules-icon">⚙️</span>
              Alert Rules
            </h1>
            <HelpIcon content="Alert Rules decide which surface changes alarm you, and how you hear about them. Turning a rule's Alarm off means 'don't alarm me about this' — findings matched by no rule still alarm at your global severity threshold." category="context" />
          </div>
          <p className="drift-rules-subtitle">
            Configure which surface changes alarm you, and how you hear about them.
            {!driftRulesLoading && (
              <span className="drift-rules-stats"> {totalAlarming} of {driftRules.filter(r => !r.comingSoon).length} rules alarming</span>
            )}
          </p>

          <div className="drift-rules-global-threshold">
            <label htmlFor="da-sev-threshold">Alarm me at severity</label>
            <select
              id="da-sev-threshold"
              value={currentThreshold}
              onChange={(e) => handleSeverityThresholdChange(e.target.value)}
              disabled={thresholdSaving}
            >
              {SEVERITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className="drift-rules-threshold-hint">
              Findings below this never alarm, even if a rule matches. Turning a rule's Alarm off means "don't alarm me about this."
            </span>
          </div>

          {actionError && (
            <div className="drift-rules-action-error" role="alert">{actionError}</div>
          )}
        </div>

        <div className="drift-rules-content">
          {driftRulesLoading ? (
            <div className="settings-card">
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading alert rules...</span>
              </div>
            </div>
          ) : driftRulesError ? (
            <div className="settings-card" style={{ borderLeft: '3px solid var(--severity-high, #ef4444)' }}>
              <p style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>{driftRulesError}</p>
              <button className="cm-action-btn" onClick={loadDriftRules}>
                Retry
              </button>
            </div>
          ) : (
            <>
              {orderedPacks.map((packName) => {
                const packRules = rulesByPack[packName];
                const alarmCount = packRules.filter(r => r.alarmEnabled && !r.comingSoon).length;
                const isPackExpanded = expandedPacks[packName];

                return (
                  <div key={packName} className="settings-card drift-pack-card" data-enabled={alarmCount > 0 ? 'true' : 'false'}>
                    <div className="drift-pack-header">
                      <div className="drift-pack-info">
                        <div className="drift-pack-title-row">
                          <h3 className="drift-pack-name">{packName}</h3>
                          <span className="drift-pack-count">{alarmCount}/{packRules.length} alarming</span>
                        </div>
                        <p className="drift-pack-description">{PACK_DESCRIPTIONS[packName] || ''}</p>
                      </div>
                      <div className="drift-pack-controls">
                        <button
                          className="drift-pack-expand-btn"
                          onClick={() => setExpandedPacks(prev => ({ ...prev, [packName]: !prev[packName] }))}
                          aria-expanded={isPackExpanded ? 'true' : 'false'}
                        >
                          {isPackExpanded ? '▲' : '▼'}
                        </button>
                      </div>
                    </div>

                    {isPackExpanded && (
                      <div className="drift-pack-rules">
                        {packRules.map(rule => {
                          const isExpanded = expandedRules[rule.ruleId];
                          const isSaving = driftRuleSaving === rule.ruleId;
                          const certOptions = Array.isArray(rule.conditionValueOptions) ? rule.conditionValueOptions : [];
                          return (
                            <div key={rule.ruleId} className="drift-rule-row" data-coming-soon={rule.comingSoon ? 'true' : 'false'}>
                              <div className="drift-rule-main">
                                <span className="drift-severity-dot" data-severity={rule.severity}></span>
                                <span className="drift-rule-name">{rule.name}</span>
                                {rule.comingSoon && (
                                  <span className="drift-rule-comingsoon-chip">coming soon</span>
                                )}
                                {rule.triggerCount > 0 && (
                                  <span className="drift-rule-trigger-count">Triggered {rule.triggerCount}×</span>
                                )}
                                <button
                                  className="drift-rule-expand-btn"
                                  onClick={() => setExpandedRules(prev => ({ ...prev, [rule.ruleId]: !prev[rule.ruleId] }))}
                                  aria-label={isExpanded ? 'Collapse rule details' : 'Expand rule details'}
                                >
                                  {isExpanded ? '−' : '+'}
                                </button>
                                {rule.type === 'custom' && (
                                  <button
                                    className="drift-rule-delete-btn"
                                    onClick={() => handleDeleteRule(rule.ruleId)}
                                    disabled={isSaving}
                                    title="Delete custom rule"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>

                              <div className="drift-rule-controls">
                                <label
                                  className="toggle-label drift-rule-control"
                                  title={rule.comingSoon ? 'Coming soon' : "off = don't alarm me about this"}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!rule.alarmEnabled}
                                    disabled={rule.comingSoon || isSaving}
                                    onChange={(e) => handleAlarmToggle(rule, e.target.checked)}
                                  />
                                  <span className="drift-rule-control-text">Alarm</span>
                                </label>
                                <label
                                  className="toggle-label drift-rule-control"
                                  title={rule.comingSoon ? 'Coming soon' : (!rule.alarmEnabled ? 'Enable the alarm first' : 'Email me when this rule matches')}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!rule.emailEnabled}
                                    disabled={rule.comingSoon || !rule.alarmEnabled || isSaving}
                                    onChange={(e) => handleEmailToggle(rule, e.target.checked)}
                                  />
                                  <span className="drift-rule-control-text">Email</span>
                                </label>
                                {rule.emailEnabled && !rule.comingSoon && (
                                  <div className="drift-rule-cadence" role="group" aria-label="Email cadence">
                                    <button
                                      type="button"
                                      className={rule.emailCadence === 'instant' ? 'active' : ''}
                                      disabled={isSaving}
                                      onClick={() => handleCadenceChange(rule, 'instant')}
                                    >
                                      Instant
                                    </button>
                                    <button
                                      type="button"
                                      className={rule.emailCadence === 'digest' ? 'active' : ''}
                                      disabled={isSaving}
                                      onClick={() => handleCadenceChange(rule, 'digest')}
                                    >
                                      Digest
                                    </button>
                                  </div>
                                )}
                                {rule.lastTriggeredAt && (
                                  <span className="drift-rule-lasttriggered">last {relativeTime(rule.lastTriggeredAt)}</span>
                                )}
                              </div>

                              {isExpanded && (
                                <div className="drift-rule-details">
                                  <p className="drift-rule-description">{rule.description}</p>
                                  {certOptions.length > 0 && (
                                    <div className="drift-rule-cert-threshold">
                                      <label htmlFor={`cert-thresh-${rule.ruleId}`}>Alert when expiring within</label>
                                      <select
                                        id={`cert-thresh-${rule.ruleId}`}
                                        value={certOptions.map(String).includes(String(rule.conditionValue)) ? String(rule.conditionValue) : String(certOptions[0])}
                                        disabled={rule.comingSoon || isSaving}
                                        onChange={(e) => handleCertThresholdChange(rule, e.target.value)}
                                      >
                                        {certOptions.map(d => (
                                          <option key={d} value={String(d)}>{d} days</option>
                                        ))}
                                      </select>
                                      <span className="drift-field-hint">By-day matching coming soon — today this alarms on any expiring or expired certificate.</span>
                                    </div>
                                  )}
                                  {rule.conditionType === 'risk_score_delta' && (() => {
                                    try {
                                      const config = JSON.parse(rule.conditionValue);
                                      return (
                                        <p className="drift-rule-config">
                                          Threshold: {config.threshold} points | Direction: {config.direction === 'drop' ? 'Score drops (worsening)' : 'Score rises (improving)'}
                                        </p>
                                      );
                                    } catch {
                                      return rule.conditionValue ? <p className="drift-rule-config">Threshold: {rule.conditionValue} points</p> : null;
                                    }
                                  })()}
                                  {rule.conditionType === 'risk_score_threshold' && rule.conditionValue && (
                                    <p className="drift-rule-config">Alert when score is at or below: {rule.conditionValue}</p>
                                  )}
                                  <div className="drift-rule-meta">
                                    <span>Severity: <span className="drift-severity-dot" data-severity={rule.severity}></span>{rule.severity}</span>
                                    <span>Scope: {rule.scope === 'all' ? 'All assets' : 'Specific assets'}</span>
                                    {rule.lastTriggeredAt && (
                                      <span>Last triggered: {new Date(rule.lastTriggeredAt).toLocaleDateString()}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Custom Rule Builder — available to all tiers */}
              {showRuleBuilder ? (
                <DriftRuleBuilder
                  onCreated={() => {
                    setShowRuleBuilder(false);
                    getRules().then(setDriftRules);
                  }}
                  onCancel={() => setShowRuleBuilder(false)}
                />
              ) : (
                <button
                  className="cm-action-btn"
                  onClick={() => setShowRuleBuilder(true)}
                  style={{ marginTop: '8px' }}
                >
                  + Create Custom Rule
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </CleanModernLayout>
  );
}

export default DriftRules;
